import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faUpload, faCheckCircle, faExclamationTriangle, faTimes, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import axios from '../../Api';
import SearchableSelect from '../../components/SearchableSelect';
import SearchableMultiSelect from '../../components/SearchableMultiSelect';

const RespControleAiUpload = () => {
  const [surveys, setSurveys] = useState([]);
  const [surveyId, setSurveyId] = useState(null);
  const [file, setFile] = useState(null);
  const [controllers, setControllers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedControllerIds, setSelectedControllerIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showTemplate, setShowTemplate] = useState(false);
  const templateDismiss = useModalDismiss(() => setShowTemplate(false), { enabled: showTemplate });

  useEffect(() => {
    axios.get('/control/resp/surveys').then(r => setSurveys(r.data.surveys || [])).catch(() => {});
    axios.get('/control/resp/ai/assignees').then(r => {
      setControllers(r.data.controllers || []);
      setUsers(r.data.users || []);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!surveyId) { setError('Sélectionnez une enquête.'); return; }
    if (!file) { setError('Choisissez un fichier Excel.'); return; }

    const fd = new FormData();
    fd.append('survey_id', surveyId);
    fd.append('file', file);
    selectedControllerIds.forEach(id => fd.append('assignee_controller_ids[]', id));
    selectedUserIds.forEach(id => fd.append('assignee_user_ids[]', id));

    try {
      setUploading(true);
      const r = await axios.post('/control/resp/ai/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(r.data);
      setFile(null);
      // Reset the file input
      const input = document.getElementById('ai-file-input');
      if (input) input.value = '';
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'upload.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faRobot} className="text-red-600 text-xl" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">Contrôle IA — Uploader</h1>
            <p className="text-sm text-gray-600">Importer un fichier Excel de contrôles générés par l'IA.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowTemplate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-red-200 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
            title="Voir le format de fichier attendu"
          >
            <FontAwesomeIcon icon={faFileExcel} />
            Template
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Enquête</label>
            <SearchableSelect
              options={surveys}
              selectedValue={surveyId}
              onChange={(v) => setSurveyId(v)}
              placeholder="-- Sélectionner une enquête --"
              searchPlaceholder="Rechercher par titre ou ID..."
              getOptionLabel={(s) => s.title}
              getOptionMeta={(s) => s.limesurvey_id ? `ID: ${s.limesurvey_id}` : null}
              maxHeight="250px"
              triggerClassName="px-3 py-2"
              textClassName="text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fichier Excel (.xlsx / .xls)</label>
            <input
              id="ai-file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files[0] || null)}
              className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
            />
            <p className="text-xs text-gray-500 mt-1">
              Colonnes attendues: <code className="bg-gray-100 px-1 rounded">id</code>,{' '}
              <code className="bg-gray-100 px-1 rounded">Statut</code>,{' '}
              <code className="bg-gray-100 px-1 rounded">Score</code>,{' '}
              <code className="bg-gray-100 px-1 rounded">Raison</code>,{' '}
              <code className="bg-gray-100 px-1 rounded">Flags</code>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigner les contrôles "À vérifier / À surveiller" à (répartis équitablement)
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Contrôleurs</div>
                <SearchableMultiSelect
                  options={controllers}
                  selectedValues={selectedControllerIds}
                  onChange={setSelectedControllerIds}
                  placeholder="-- Aucun contrôleur --"
                  searchPlaceholder="Rechercher par nom ou réf..."
                  getOptionLabel={(c) => c.name}
                  getOptionMeta={(c) => c.ref || null}
                  selectedCountLabel={(n) => `${n} contrôleur${n > 1 ? 's' : ''} sélectionné${n > 1 ? 's' : ''}`}
                  maxHeight="240px"
                  triggerClassName="px-3 py-2"
                  textClassName="text-sm"
                />
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Resp. Contrôle</div>
                <SearchableMultiSelect
                  options={users}
                  selectedValues={selectedUserIds}
                  onChange={setSelectedUserIds}
                  placeholder="-- Aucun resp_controle --"
                  searchPlaceholder="Rechercher par nom..."
                  getOptionLabel={(u) => u.name}
                  getOptionMeta={(u) => u.ref || null}
                  selectedCountLabel={(n) => `${n} resp. contrôle sélectionné${n > 1 ? 's' : ''}`}
                  maxHeight="240px"
                  triggerClassName="px-3 py-2"
                  textClassName="text-sm"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Les contrôles "OK" et "À rejeter" sont appliqués directement et ne nécessitent pas d'assignation.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2">
              <FontAwesomeIcon icon={faTimes} className="mt-1" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faUpload} />
            {uploading ? 'Traitement...' : 'Importer'}
          </button>
        </form>

        {result && (
          <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Résultat de l'import</h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="text-xs text-green-700">Insérés</div>
                <div className="text-2xl font-bold text-green-800 flex items-center gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} /> {result.inserted}
                </div>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <div className="text-xs text-yellow-700">Ignorés</div>
                <div className="text-2xl font-bold text-yellow-800">{result.skipped}</div>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="text-xs text-red-700">Erreurs</div>
                <div className="text-2xl font-bold text-red-800">{(result.errors || []).length}</div>
              </div>
            </div>

            {(result.errors || []).length > 0 && (
              <div>
                <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500" />
                  Détails des erreurs
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Ligne</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Raison</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((e, i) => (
                        <tr key={i} className="border-t border-gray-200">
                          <td className="px-3 py-2 text-gray-800">{e.row}</td>
                          <td className="px-3 py-2 text-red-700">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showTemplate && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          {...templateDismiss}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faFileExcel} className="text-red-600" />
                <h2 className="text-xl font-bold text-gray-800">Format de fichier attendu</h2>
              </div>
              <button
                onClick={() => setShowTemplate(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Le fichier Excel doit contenir les 5 colonnes suivantes (les noms et valeurs ne sont
              pas sensibles à la casse ni aux accents).
            </p>

            <div className="overflow-x-auto border border-gray-200 rounded-lg mb-5">
              <table className="w-full text-sm">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-red-800 uppercase">Colonne</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-red-800 uppercase">Description</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-red-800 uppercase">Valeurs acceptées</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-3 py-2 font-mono text-gray-900">id</td>
                    <td className="px-3 py-2 text-gray-700">ID de la soumission (response_id LimeSurvey)</td>
                    <td className="px-3 py-2 text-gray-600">Numérique — ex: <span className="font-mono">1057</span></td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-gray-900">Statut</td>
                    <td className="px-3 py-2 text-gray-700">Décision du contrôle</td>
                    <td className="px-3 py-2 text-gray-600">
                      <div className="space-y-1.5">
                        <div className="flex items-baseline gap-2">
                          <code className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded shrink-0">OK</code>
                          <span className="text-xs text-gray-600">— Soumission validée</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <code className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded shrink-0">À REJETER</code>
                          <span className="text-xs text-gray-600">— Soumission à rejeter/Supprimer</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <code className="bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded shrink-0">À VÉRIFIER</code>
                          <span className="text-xs text-gray-600">— Mis en attente</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <code className="bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded shrink-0">À SURVEILLER</code>
                          <span className="text-xs text-gray-600">— Mis en attente</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-gray-900">score</td>
                    <td className="px-3 py-2 text-gray-700">Note de qualité (obligatoire sauf pour À VÉRIFIER/À SURVEILLER)</td>
                    <td className="px-3 py-2 text-gray-600">Entier <span className="font-mono">1</span> à <span className="font-mono">5</span></td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-gray-900">raison</td>
                    <td className="px-3 py-2 text-gray-700">Catégorie du problème</td>
                    <td className="px-3 py-2 text-gray-600">
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded mr-1">métier</code>
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded mr-1">projet</code>
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded mr-1">métier et projet</code>
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded mr-1">les deux</code>
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded">rien</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-gray-900">Flags</td>
                    <td className="px-3 py-2 text-gray-700">
                      Commentaire libre, ou motif de rejet si Statut = À REJETER
                    </td>
                    <td className="px-3 py-2 text-gray-600">Texte libre</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mb-3 text-sm font-semibold text-gray-700">Exemple :</div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">id</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Statut</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">score</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">raison</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Flags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <tr>
                    <td className="px-3 py-2 font-mono">1057</td>
                    <td className="px-3 py-2">OK</td>
                    <td className="px-3 py-2 font-mono">5</td>
                    <td className="px-3 py-2">rien</td>
                    <td className="px-3 py-2 text-gray-500">—</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">1058</td>
                    <td className="px-3 py-2">À REJETER</td>
                    <td className="px-3 py-2 font-mono">2</td>
                    <td className="px-3 py-2">métier</td>
                    <td className="px-3 py-2 text-gray-700">Réponses incohérentes</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">1059</td>
                    <td className="px-3 py-2">À VÉRIFIER</td>
                    <td className="px-3 py-2 text-gray-400">—</td>
                    <td className="px-3 py-2">projet</td>
                    <td className="px-3 py-2 text-gray-700">À revoir avec l'enquêteur</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setShowTemplate(false)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RespControleAiUpload;
